import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormControlLabel,
  Checkbox,
  Switch,
  DialogContentText,
  Fab,
  Snackbar,
  Alert,
  useScrollTrigger,
  Zoom,
  Tooltip
} from '@mui/material';
import {
  Search,
  Add,
  Business,
  Clear,
  Edit,
  Delete,
  ContentCopy,
  MyLocation,
  KeyboardArrowUp,
  Download,
} from '@mui/icons-material';
import { WaterDrop, Bolt, VpnKey, QrCode2 } from '@mui/icons-material';
import QRCode from 'react-qr-code';
import DeviceCard from '../components/DeviceCard';
import StructuredLocationFields from '../components/StructuredLocationFields';
import deviceService from '../services/deviceService';
import siteService from '../services/siteService';
import { useRefresh } from '../context/RefreshContext';
import { useAuth } from '../context/AuthContext';

// Unit helper per device type
const getDeviceUnit = (d) => (d?.deviceType === 'electrical_meter' ? 'kWh' : 'L');

// Scroll-to-top button component
function ScrollTop(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector('#back-to-top-anchor');
    if (anchor) {
      anchor.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 10 }}
      >
        {children}
      </Box>
    </Zoom>
  );
}

const Devices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { refreshCounter, refreshData } = useRefresh();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const isAdminOrOperator = isAdmin || authUser?.role === 'operator';
  const isAdminOrSuper = isAdminOrOperator || authUser?.role === 'super_user';
  const isEndUser = authUser?.role === 'end_user';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [devicesCsvExporting, setDevicesCsvExporting] = useState(false);
  const [sites, setSites] = useState([]);
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [parentDeviceOptions, setParentDeviceOptions] = useState([]);
  const [parentDeviceOptionsLoading, setParentDeviceOptionsLoading] = useState(false);
  const restorePayload = useMemo(() => location?.state?.restore || null, [location?.state]);
  const restoreScrollY = location?.state?.restoreScrollY;
  const didRestoreRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState(restorePayload?.searchTerm || '');
  const [filterSiteIds, setFilterSiteIds] = useState(restorePayload?.filterSiteIds || []);
  const [filterDeviceTypes, setFilterDeviceTypes] = useState(restorePayload?.filterDeviceTypes || []);
  const [filterIsActive, setFilterIsActive] = useState(restorePayload?.filterIsActive || '');
  const [sortBy, setSortBy] = useState(restorePayload?.sortBy || '');
  const [sortDir, setSortDir] = useState(restorePayload?.sortDir || 'asc');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [currentDevice, setCurrentDevice] = useState(null);
  const [deviceUsageData, setDeviceUsageData] = useState({});
  const [deviceFormData, setDeviceFormData] = useState({
    serialNumber: '',
    accountNumber: '',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    siteIds: [],
    parentId: '',
    deviceType: 'water_meter',
    isActive: true,
    multiplier: 1,
    highUsageThresholdLiters: '2',
    dailyThresholdLiters: '',
    decryptKey: ''
  });
  // Paging state
  const [page, setPage] = useState(restorePayload?.page || 1);
  const [pageSize, setPageSize] = useState(restorePayload?.pageSize || 50);
  const [totalDevices, setTotalDevices] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [gpsLocating, setGpsLocating] = useState(false);
  const [multiplierPromptOpen, setMultiplierPromptOpen] = useState(false);
  const [multiplierPromptSaving, setMultiplierPromptSaving] = useState(false);
  const [multiplierApplyMode, setMultiplierApplyMode] = useState('');
  const [multiplierEarliestDate, setMultiplierEarliestDate] = useState('');
  const [multiplierCustomDate, setMultiplierCustomDate] = useState('');
  const [multiplierCustomEndDate, setMultiplierCustomEndDate] = useState('');
  const [recalculateUsageSaving, setRecalculateUsageSaving] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
  const [multiplierPreview, setMultiplierPreview] = useState({
    loading: false,
    error: '',
    previousMultiplier: 1,
    nextMultiplier: 1,
    rows: []
  });

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteDeviceLabel, setInviteDeviceLabel] = useState('');
  const [inviteExpiresAt, setInviteExpiresAt] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateInviteLink = async (device, event) => {
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
    if (!device?.id) return;

    try {
      const resp = await deviceService.createDeviceInvite(device.id);
      const token = resp?.token;
      if (!token) {
        showSnackbar('Failed to generate invite link', 'error');
        return;
      }
      const url = `${window.location.origin}/device-invite/${token}`;

      setInviteUrl(url);
      setInviteDeviceLabel(device?.description || device?.serialNumber || 'Device');
      setInviteExpiresAt(resp?.expiresAt || '');
      setInviteDialogOpen(true);

      try {
        await navigator.clipboard.writeText(url);
        showSnackbar('Invite link copied to clipboard', 'success');
      } catch (e) {
        // If clipboard fails, the dialog still shows the URL.
      }
    } catch (err) {
      showSnackbar(err?.message || 'Failed to generate invite link', 'error');
    }
  };

  const handleCopyInviteUrl = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      showSnackbar('Invite link copied to clipboard', 'success');
    } catch (e) {
      showSnackbar('Unable to copy invite link', 'error');
    }
  };
  // Bulk edit dialog state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const [bulkSearchResults, setBulkSearchResults] = useState([]);
  const [bulkSelectedDevices, setBulkSelectedDevices] = useState([]);
  const [bulkSite, setBulkSite] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkFields, setBulkFields] = useState({
    description: false,
    location: false,
    deviceType: false,
    isActive: false,
    multiplier: false,
    dailyThresholdLiters: false,
    parentId: false,
    siteIds: false
  });
  const [bulkValues, setBulkValues] = useState({
    description: '',
    location: '',
    deviceType: 'water_meter',
    isActive: true,
    multiplier: '',
    multiplierDeviceDateMode: 'before_or_on',
    multiplierDeviceDateFrom: '',
    multiplierDeviceDateTo: '',
    dailyThresholdLiters: '',
    parentId: '',
    siteIds: []
  });


  // Function to load all data
  const loadData = async (pageArg = page, pageSizeArg = pageSize, search = '') => {
    setLoading(true);
    try {
      // Fetch all sites
      const sitesData = await siteService.fetchSites();
      setSites(sitesData);

      let devicesData = [];
      let total = 0;
      let filteredDevicesLocal = [];

       const serverSortable = new Set([
         'serialNumber',
         'description',
         'location',
         'deviceType',
         'isActive',
         'lastReading',
         'firstReportedAt',
         'multiplier',
         'dailyThresholdLiters',
         'site'
       ]);

       const compareDevices = (a, b, usageData = deviceUsageData) => {
         const dirFactor = String(sortDir).toLowerCase() === 'desc' ? -1 : 1;
         const normalizeString = (v) => (v == null ? null : String(v).toLowerCase());
         const normalizeNumber = (v) => {
           const n = Number(v);
           return Number.isFinite(n) ? n : null;
         };
         const normalizeDate = (v) => {
           if (!v) return null;
           const t = new Date(v).getTime();
           return Number.isFinite(t) ? t : null;
         };

         const getValue = (d) => {
           if (sortBy === 'site') return normalizeString(d?.sites?.[0]?.description || '');
           if (sortBy === 'firstReportedAt') return normalizeDate(d?.firstReportedAt);
           if (sortBy === 'lastReading') return normalizeDate(d?.lastReading);
           if (sortBy === 'isActive') return d?.isActive ? 1 : 0;
           if (sortBy === 'multiplier') return normalizeNumber(d?.multiplier);
           if (sortBy === 'dailyThresholdLiters') return normalizeNumber(d?.dailyThresholdLiters);
           if (sortBy === 'todayReadingCount') return normalizeNumber(usageData?.[d?.id]?.todayReadingCount);
           if (sortBy === 'todayUsage') return normalizeNumber(usageData?.[d?.id]?.todayUsage);
           if (sortBy === 'yesterdayUsage') return normalizeNumber(usageData?.[d?.id]?.yesterdayUsage);
           if (sortBy === 'last30DaysUsage') return normalizeNumber(usageData?.[d?.id]?.last30DaysUsage);

           if (sortBy === 'serialNumber') return normalizeString(d?.serialNumber);
           if (sortBy === 'description') return normalizeString(d?.description);
           if (sortBy === 'location') return normalizeString(d?.location);
           if (sortBy === 'deviceType') return normalizeString(d?.deviceType);

           return null;
         };

         const av = getValue(a);
         const bv = getValue(b);

         if (av == null && bv == null) {
           const ad = normalizeString(a?.description || '');
           const bd = normalizeString(b?.description || '');
           if (ad !== bd) return ad.localeCompare(bd);
           const as = normalizeString(a?.serialNumber || '');
           const bs = normalizeString(b?.serialNumber || '');
           return as.localeCompare(bs);
         }
         if (av == null) return 1;
         if (bv == null) return -1;

         if (typeof av === 'string' || typeof bv === 'string') {
           return dirFactor * String(av).localeCompare(String(bv));
         }

         const delta = Number(av) - Number(bv);
         if (delta === 0) return 0;
         return dirFactor * (delta > 0 ? 1 : -1);
       };

      const hasDropdownFilters =
        (Array.isArray(filterSiteIds) && filterSiteIds.length > 0)
        || (Array.isArray(filterDeviceTypes) && filterDeviceTypes.length > 0)
        || (String(filterIsActive || '').trim() !== '');

      // If you have dropdown filters active, fetch ALL devices (lean) and do client-side filtering + paging.
      // This guarantees filters affect all pages (not only the current page).
      const shouldClientFilterAll = search.trim() === '' && hasDropdownFilters;

      if (search.trim() !== '' || shouldClientFilterAll) {
        // Search mode OR dropdown-filter mode: fetch all devices (up to 10,000) in lean mode
        const resp = await deviceService.fetchDevices({ limit: 10000, lean: true });
        devicesData = resp.devices || [];

        if (Array.isArray(filterSiteIds) && filterSiteIds.length > 0) {
          const idSet = new Set(filterSiteIds.map((id) => String(id)));
          const includeUnassigned = idSet.has('__unassigned__');
          devicesData = devicesData.filter((d) => {
            const siteIds = Array.isArray(d?.sites) ? d.sites.map((s) => String(s?.id)) : [];
            if (siteIds.length === 0) return includeUnassigned;
            return siteIds.some((id) => idSet.has(id));
          });
        }

        if (Array.isArray(filterDeviceTypes) && filterDeviceTypes.length > 0) {
          const typesSet = new Set(filterDeviceTypes.map((t) => String(t)));
          devicesData = devicesData.filter((d) => typesSet.has(String(d?.deviceType || '')));
        }

        const isActiveStr = String(filterIsActive || '').trim().toLowerCase();
        if (isActiveStr === 'true' || isActiveStr === '1') {
          devicesData = devicesData.filter((d) => d?.isActive !== false);
        } else if (isActiveStr === 'false' || isActiveStr === '0') {
          devicesData = devicesData.filter((d) => d?.isActive === false);
        }

        if (search.trim() !== '') {
          // Filter client-side by search term
          const term = search.toLowerCase();
          devicesData = devicesData.filter(device => 
            (device.description && device.description.toLowerCase().includes(term)) || 
            (device.serialNumber && device.serialNumber.toLowerCase().includes(term)) ||
            (device.location && device.location.toLowerCase().includes(term)) ||
            (Array.isArray(device.sites) && device.sites.some((s) => s?.description && s.description.toLowerCase().includes(term)))
          );
        }

        if (sortBy && (serverSortable.has(sortBy) || sortBy === 'site')) {
          devicesData = [...devicesData].sort((a, b) => compareDevices(a, b, {}));
        }
        total = devicesData.length;
        // Apply paging to filtered results
        const startIdx = (pageArg - 1) * pageSizeArg;
        const endIdx = startIdx + pageSizeArg;
        filteredDevicesLocal = devicesData.slice(startIdx, endIdx);
        setDevices(devicesData);
        setFilteredDevices(filteredDevicesLocal);
        setTotalDevices(total);
      } else {
        // Fetch paged devices
        const resp = await deviceService.fetchDevices({
          limit: pageSizeArg,
          offset: (pageArg - 1) * pageSizeArg,
          lean: true,
          siteIds: (Array.isArray(filterSiteIds) && filterSiteIds.length > 0)
            ? filterSiteIds.join(',')
            : undefined,
          deviceTypes: (Array.isArray(filterDeviceTypes) && filterDeviceTypes.length > 0)
            ? filterDeviceTypes.join(',')
            : undefined,
          isActive: String(filterIsActive || '').trim() !== ''
            ? filterIsActive
            : undefined,
          sortBy: (sortBy && serverSortable.has(sortBy)) ? sortBy : undefined,
          sortDir: (sortBy && serverSortable.has(sortBy)) ? sortDir : undefined
        });
        devicesData = resp.devices || [];
        filteredDevicesLocal = devicesData;
        setDevices(devicesData);
        setFilteredDevices(filteredDevicesLocal);
        setTotalDevices(resp.total || devicesData.length);
      }

      const usageData = {};
      try {
        const idsForUsage = (filteredDevicesLocal || []).map(d => d.id);
        if (idsForUsage.length > 0) {
          const endDateStr = new Date().toISOString().split('T')[0];
          const startDateObj = new Date();
          startDateObj.setDate(startDateObj.getDate() - 30);
          const startDateStr = startDateObj.toISOString().split('T')[0];
          const bulk = await deviceService.getDevicesDailyUsageBulk({ deviceIds: idsForUsage, startDate: startDateStr, endDate: endDateStr });
          const todayStr = endDateStr;
          const yestStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
          (bulk.devices || []).forEach(entry => {
            let today = 0, yesterday = 0, last30 = 0;
            (entry.daily || []).forEach(d => {
              const val = (Number(d.totalUsage) || 0);
              last30 += val;
              if (d.date === todayStr) today = val;
              if (d.date === yestStr) yesterday = val;
            });
            usageData[entry.deviceId] = {
              todayUsage: today,
              yesterdayUsage: yesterday,
              last30DaysUsage: last30,
              todayReadingCount: 0
            };
          });
        }
      } catch (e) {
        console.error('Failed to fetch bulk daily usage:', e);
      }

      // Fetch accurate today's reading counts in bulk for displayed devices
      try {
        const todayStr = new Date().toISOString().split('T')[0];
        const idsForCounts = (filteredDevicesLocal || []).map(d => d.id);
        if (idsForCounts.length > 0) {
          const countsResp = await deviceService.fetchDailyReadingCounts({ date: todayStr, deviceIds: idsForCounts });
          const countsMap = new Map(
            (countsResp.counts || []).map(item => [item.deviceId, item.readingCount || 0])
          );
          idsForCounts.forEach(id => {
            const count = countsMap.get(id) || 0;
            if (!usageData[id]) usageData[id] = { todayUsage: 0, yesterdayUsage: 0, last30DaysUsage: 0, todayReadingCount: 0 };
            usageData[id].todayReadingCount = count;
          });
        }
      } catch (e) {
        console.error('Failed to fetch bulk daily reading counts:', e);
        // Fallback: keep heuristic counts already computed
      }

      setDeviceUsageData(usageData);

      if (sortBy && !serverSortable.has(sortBy)) {
        const sorted = [...filteredDevicesLocal].sort((a, b) => compareDevices(a, b, usageData));
        setFilteredDevices(sorted);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const mergeDevicesById = (a, b) => {
    const map = new Map();
    (Array.isArray(a) ? a : []).forEach((d) => {
      if (d?.id != null) map.set(String(d.id), d);
    });
    (Array.isArray(b) ? b : []).forEach((d) => {
      if (d?.id != null) map.set(String(d.id), d);
    });
    return Array.from(map.values());
  };

  const toDateOnlyString = (value) => {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const parsed = new Date(raw);
    if (!Number.isFinite(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10);
  };

  const getBulkMultiplierDateFilteredSelection = () => {
    const selected = Array.isArray(bulkSelectedDevices) ? bulkSelectedDevices : [];
    const mode = String(bulkValues.multiplierDeviceDateMode || 'before_or_on');
    const fromDate = toDateOnlyString(bulkValues.multiplierDeviceDateFrom);
    const toDate = toDateOnlyString(bulkValues.multiplierDeviceDateTo);
    const summary = {
      selectedCount: selected.length,
      matchingDevices: selected,
      matchingCount: selected.length,
      missingDateCount: 0,
      error: ''
    };

    if (!bulkFields.multiplier) {
      return summary;
    }

    if ((mode === 'before_or_on' || mode === 'after_or_on') && !fromDate) {
      return { ...summary, error: 'Please select a device date for this multiplier filter.' };
    }
    if (mode === 'between') {
      if (!fromDate || !toDate) {
        return { ...summary, error: 'Please select both from and to dates for the range filter.' };
      }
      if (toDate < fromDate) {
        return { ...summary, error: 'Range end date cannot be earlier than range start date.' };
      }
    }

    const matchingDevices = [];
    let missingDateCount = 0;
    for (const device of selected) {
      const firstReportedDate = toDateOnlyString(device?.firstReportedAt);
      if (!firstReportedDate) {
        missingDateCount += 1;
        continue;
      }
      if (mode === 'before_or_on' && firstReportedDate <= fromDate) {
        matchingDevices.push(device);
      } else if (mode === 'after_or_on' && firstReportedDate >= fromDate) {
        matchingDevices.push(device);
      } else if (mode === 'between' && firstReportedDate >= fromDate && firstReportedDate <= toDate) {
        matchingDevices.push(device);
      }
    }

    return {
      ...summary,
      matchingDevices,
      matchingCount: matchingDevices.length,
      missingDateCount
    };
  };

  const bulkMultiplierSelection = getBulkMultiplierDateFilteredSelection();
  const bulkApplyTargetCount = bulkFields.multiplier ? bulkMultiplierSelection.matchingCount : bulkSelectedDevices.length;

  const runBulkSearch = async (options = {}) => {
    try {
      setBulkLoading(true);
      // Fetch all devices (lean) and filter client-side, ensures searching ALL devices
      const resp = await deviceService.fetchDevices({ limit: 10000, lean: true });
      let list = resp.devices || [];
      const term = (bulkSearchTerm || '').toLowerCase();
      if (term) {
        list = list.filter(device =>
          (device.description && device.description.toLowerCase().includes(term)) ||
          (device.serialNumber && device.serialNumber.toLowerCase().includes(term)) ||
          (device.location && device.location.toLowerCase().includes(term)) ||
          (Array.isArray(device.sites) && device.sites.some((s) => s?.description && s.description.toLowerCase().includes(term)))
        );
      }
      setBulkSearchResults(list);

      if (options?.setSelection) {
        setBulkSelectedDevices(list);
      }
    } catch (e) {
      console.error('Bulk search failed:', e);
      setSnackbar({ open: true, message: e.message || 'Bulk search failed', severity: 'error' });
    } finally {
      setBulkLoading(false);
    }
  };

  const loadBulkSiteDevices = async (siteId) => {
    const id = siteId != null ? String(siteId) : '';
    if (!id) return [];

    try {
      setBulkLoading(true);
      const siteDevices = await deviceService.fetchDevicesBySite(id);
      return Array.isArray(siteDevices) ? siteDevices : [];
    } catch (e) {
      console.error('Load site devices failed:', e);
      setSnackbar({ open: true, message: e.message || 'Load site devices failed', severity: 'error' });
      return [];
    } finally {
      setBulkLoading(false);
    }
  };

  const isBulkDeviceSelected = (deviceId) => {
    const id = deviceId != null ? String(deviceId) : '';
    if (!id) return false;
    return bulkSelectedDevices.some((device) => String(device?.id) === id);
  };

  const toggleBulkDeviceSelection = (device, shouldSelect) => {
    if (!device?.id) return;
    if (shouldSelect) {
      setBulkSelectedDevices((prev) => mergeDevicesById(prev, [device]));
      return;
    }

    const id = String(device.id);
    setBulkSelectedDevices((prev) => prev.filter((selectedDevice) => String(selectedDevice?.id) !== id));
  };

  const openBulkDialog = async () => {
    if (isEndUser || !isAdminOrOperator) return;
    setBulkDialogOpen(true);
    setBulkSelectedDevices([]);
    // Default search (empty) to load all devices quickly
    await runBulkSearch();
  };

  useEffect(() => {
    loadData(page, pageSize, searchTerm);
    // Add refreshCounter as a dependency to reload data when refresh is triggered
  }, [refreshCounter, page, pageSize, searchTerm, filterSiteIds, filterDeviceTypes, filterIsActive, sortBy, sortDir]);

  useEffect(() => {
    if (didRestoreRef.current) return;
    if (!restorePayload) return;

    didRestoreRef.current = true;
    if (restorePayload.searchTerm !== undefined) setSearchTerm(restorePayload.searchTerm || '');
    if (restorePayload.filterSiteIds !== undefined) setFilterSiteIds(Array.isArray(restorePayload.filterSiteIds) ? restorePayload.filterSiteIds : []);
    if (restorePayload.filterDeviceTypes !== undefined) setFilterDeviceTypes(Array.isArray(restorePayload.filterDeviceTypes) ? restorePayload.filterDeviceTypes : []);
    if (restorePayload.filterIsActive !== undefined) setFilterIsActive(restorePayload.filterIsActive || '');
    if (restorePayload.sortBy !== undefined) setSortBy(restorePayload.sortBy || '');
    if (restorePayload.sortDir !== undefined) setSortDir(restorePayload.sortDir || 'asc');
    if (restorePayload.page !== undefined) setPage(Number(restorePayload.page) || 1);
    if (restorePayload.pageSize !== undefined) setPageSize(Number(restorePayload.pageSize) || 50);

    if (typeof restoreScrollY === 'number') {
      setTimeout(() => window.scrollTo(0, restoreScrollY), 0);
    }
  }, [restorePayload, restoreScrollY]);

  useEffect(() => {
    const shouldLoadParentOptions = (openDialog && (dialogType === 'add' || dialogType === 'edit')) || bulkDialogOpen;
    if (!shouldLoadParentOptions) return;

    let cancelled = false;
    (async () => {
      try {
        setParentDeviceOptionsLoading(true);
        const resp = await deviceService.fetchDevices({ limit: 10000, lean: true });
        if (cancelled) return;
        setParentDeviceOptions(resp.devices || []);
      } catch (e) {
        console.error('Failed to load parent device options:', e);
        if (cancelled) return;
        setParentDeviceOptions([]);
      } finally {
        if (cancelled) return;
        setParentDeviceOptionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openDialog, dialogType, bulkDialogOpen]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleFilterSiteIdsChange = (event) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    setFilterSiteIds(next);
    setPage(1);
  };

  const handleFilterDeviceTypesChange = (event) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    setFilterDeviceTypes(next);
    setPage(1);
  };

  const handleFilterIsActiveChange = (event) => {
    setFilterIsActive(event.target.value);
    setPage(1);
  };

  const handleDeviceClick = (deviceId) => {
    const returnTo = `${location.pathname}${location.search || ''}`;
    navigate(`/devices/${deviceId}`, {
      state: {
        returnTo,
        returnState: { searchTerm, filterSiteIds, filterDeviceTypes, filterIsActive, sortBy, sortDir, page, pageSize },
        returnScrollY: window.scrollY
      }
    });
  };

  const requestSort = (key) => {
    const nextKey = String(key || '');
    if (!nextKey) return;
    if (sortBy === nextKey) {
      setSortDir(String(sortDir).toLowerCase() === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(nextKey);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleSiteClick = (siteId, event) => {
    event.stopPropagation();
    navigate(`/sites/${siteId}`);
  };

  const handleAddDevice = () => {
    if (isEndUser || !isAdminOrOperator) return;
    setDeviceFormData({
      serialNumber: '',
      accountNumber: '',
      description: '',
      location: '',
      latitude: '',
      longitude: '',
      siteIds: [],
      parentId: '',
      deviceType: 'water_meter',
      isActive: true,
      multiplier: 1,
      highUsageThresholdLiters: '2',
      dailyThresholdLiters: ''
    });
    setDialogType('add');
    setOpenDialog(true);
  };

  const handleEditDevice = (device, event) => {
    event.stopPropagation();
    
    // Find the site IDs from the device's sites array (a device can belong to multiple sites)
    const siteIds = Array.isArray(device.sites) ? device.sites.map((s) => s.id) : [];

    setCurrentDevice(device);
    setDeviceFormData({
      serialNumber: device.serialNumber,
      accountNumber: device.accountNumber || device.metadata?.accountNumber || '',
      description: device.description || '',
      location: device.location || '',
      latitude: device.latitude !== undefined && device.latitude !== null ? String(device.latitude) : '',
      longitude: device.longitude !== undefined && device.longitude !== null ? String(device.longitude) : '',
      siteIds: siteIds,
      parentId: device.parentId || null,
      deviceType: device.deviceType || 'water_meter',
      isActive: device.isActive !== false, // default to true if undefined
      multiplier: device.multiplier !== undefined && device.multiplier !== null ? Number(device.multiplier) : 1,
      highUsageThresholdLiters: device.highUsageThresholdLiters !== undefined && device.highUsageThresholdLiters !== null ? Number(device.highUsageThresholdLiters) : '2',
      dailyThresholdLiters: device.dailyThresholdLiters !== undefined && device.dailyThresholdLiters !== null ? Number(device.dailyThresholdLiters) : '',
      decryptKey: device.metadata?.decryptKey || ''
    });
    setDialogType('edit');
    setOpenDialog(true);
  };
  const handleDeleteDevice = (device, event) => {
    event.stopPropagation();
    if (isEndUser || !isAdmin) return;
    setCurrentDevice(device);
    setDialogType('delete');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCloseMultiplierPrompt = () => {
    if (multiplierPromptSaving) return;
    setMultiplierPromptOpen(false);
    setPendingUpdatePayload(null);
    setMultiplierApplyMode('');
    setMultiplierEarliestDate('');
    setMultiplierCustomDate('');
    setMultiplierCustomEndDate('');
    setMultiplierPreview({
      loading: false,
      error: '',
      previousMultiplier: 1,
      nextMultiplier: 1,
      rows: []
    });
  };

  const loadMultiplierPreview = async (deviceId, previousMultiplier, nextMultiplier) => {
    setMultiplierPreview({
      loading: true,
      error: '',
      previousMultiplier,
      nextMultiplier,
      rows: []
    });

    try {
      const readings = await deviceService.fetchDeviceReadings(deviceId, { limit: 2, order: 'desc' });
      const latestTwo = Array.isArray(readings)
        ? [...readings]
            .filter((r) => r && r.readingTime)
            .sort((a, b) => new Date(a.readingTime) - new Date(b.readingTime))
            .slice(-2)
        : [];

      if (latestTwo.length < 2) {
        setMultiplierPreview({
          loading: false,
          error: 'Not enough readings to preview (need at least 2 readings).',
          previousMultiplier,
          nextMultiplier,
          rows: []
        });
        return;
      }

      const [fromReading, toReading] = latestTwo;
      const rawUsage = Math.max(0, (Number(toReading.reading) || 0) - (Number(fromReading.reading) || 0));
      const previousUsage = Number((rawUsage * previousMultiplier).toFixed(3));
      const nextUsage = Number((rawUsage * nextMultiplier).toFixed(3));

      setMultiplierPreview({
        loading: false,
        error: '',
        previousMultiplier,
        nextMultiplier,
        rows: [
          {
            fromReadingTime: fromReading.readingTime,
            toReadingTime: toReading.readingTime,
            fromReadingValue: Number(fromReading.reading) || 0,
            toReadingValue: Number(toReading.reading) || 0,
            rawUsage,
            previousUsage,
            nextUsage
          }
        ]
      });
    } catch (error) {
      setMultiplierPreview({
        loading: false,
        error: 'Unable to load preview readings.',
        previousMultiplier,
        nextMultiplier,
        rows: []
      });
    }
  };

  const handleDeviceFormChange = (event) => {
    const { name, value } = event.target;
    setDeviceFormData({
      ...deviceFormData,
      [name]: value
    });
  };

  const handleSiteSelection = (event, newValue) => {
    setDeviceFormData({
      ...deviceFormData,
      siteIds: Array.isArray(newValue) ? newValue.map((s) => s.id) : []
    });
  };

  const handleParentDeviceSelection = (event, newValue) => {
    setDeviceFormData({
      ...deviceFormData,
      parentId: newValue ? newValue.id : null
    });
  };

  const handleUseCurrentGps = async () => {
    if (gpsLocating) return;

    try {
      if (!navigator.geolocation) {
        setSnackbar({ open: true, message: 'Geolocation is not supported by this browser', severity: 'error' });
        return;
      }

      setGpsLocating(true);

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        });
      });

      const lat = position?.coords?.latitude;
      const lng = position?.coords?.longitude;

      if (typeof lat !== 'number' || typeof lng !== 'number') {
        setSnackbar({ open: true, message: 'Unable to read GPS coordinates', severity: 'error' });
        return;
      }

      setDeviceFormData((prev) => ({
        ...prev,
        latitude: lat.toFixed(7),
        longitude: lng.toFixed(7)
      }));
    } catch (e) {
      const code = e?.code;
      let msg = e?.message || 'Failed to get current GPS location';
      if (code === 1) msg = 'Location permission denied';
      if (code === 2) msg = 'Location unavailable';
      if (code === 3) msg = 'Location request timed out';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setGpsLocating(false);
    }
  };

  const handleSaveForm = async () => {
    try {
      // Prepare the data for API - convert siteId to siteIds array
      // end_user edit: threshold only
      const thresholdValue = deviceFormData.dailyThresholdLiters === '' || deviceFormData.dailyThresholdLiters === null || deviceFormData.dailyThresholdLiters === undefined
        ? null
        : Number(deviceFormData.dailyThresholdLiters);

      const highUsageValue = deviceFormData.highUsageThresholdLiters === '' || deviceFormData.highUsageThresholdLiters === null || deviceFormData.highUsageThresholdLiters === undefined
        ? null
        : Number(deviceFormData.highUsageThresholdLiters);

      const apiData = (() => {
        if (isEndUser && dialogType === 'edit') {
          return { dailyThresholdLiters: thresholdValue };
        }

        const latitude = deviceFormData.latitude === '' || deviceFormData.latitude === null || deviceFormData.latitude === undefined
          ? null
          : Number(deviceFormData.latitude);
        const longitude = deviceFormData.longitude === '' || deviceFormData.longitude === null || deviceFormData.longitude === undefined
          ? null
          : Number(deviceFormData.longitude);

        if (!isAdminOrOperator) {
          // Super users may only edit name, location, account number and thresholds
          return {
            description: deviceFormData.description,
            location: deviceFormData.location,
            latitude,
            longitude,
            accountNumber: deviceFormData.accountNumber || null,
            highUsageThresholdLiters: highUsageValue,
            dailyThresholdLiters: thresholdValue
          };
        }

        return {
          ...deviceFormData,
          multiplier: deviceFormData.multiplier !== undefined && deviceFormData.multiplier !== null ? Number(deviceFormData.multiplier) : 1,
          latitude,
          longitude,
          highUsageThresholdLiters: highUsageValue,
          dailyThresholdLiters: thresholdValue,
          // Ensure parentId is null instead of empty string
          parentId: deviceFormData.parentId || null,
          siteIds: Array.isArray(deviceFormData.siteIds) ? deviceFormData.siteIds : [],
          metadata: String(deviceFormData.decryptKey || '').trim() ? { decryptKey: String(deviceFormData.decryptKey || '').trim() } : {}
        };
      })();

      
      if (dialogType === 'add') {
        if (!isAdminOrOperator) return;
        // Call API to add a new device
        const newDevice = await deviceService.createDevice(apiData);
        
        // Update the devices list with the new device
        setDevices(prevDevices => [...prevDevices, newDevice]);
        setFilteredDevices(prevFiltered => [...prevFiltered, newDevice]);
        
        // Show success message
        alert('Device added successfully!');
      } else if (dialogType === 'edit') {
        if (isEndUser) {
          const updatedDevice = await deviceService.updateDevice(currentDevice.id, apiData);
          setDevices(prevDevices => 
            prevDevices.map(device => device.id === currentDevice.id ? { ...device, ...updatedDevice } : device)
          );
          setFilteredDevices(prevFiltered => 
            prevFiltered.map(device => device.id === currentDevice.id ? { ...device, ...updatedDevice } : device)
          );
          alert('Device threshold updated successfully!');
          setOpenDialog(false);
          loadData();
          refreshData();
          return;
        }

        if (!isAdminOrOperator) {
          const updatedDevice = await deviceService.updateDevice(currentDevice.id, apiData);
          setDevices(prevDevices =>
            prevDevices.map(device => device.id === currentDevice.id ? { ...device, ...updatedDevice } : device)
          );
          setFilteredDevices(prevFiltered =>
            prevFiltered.map(device => device.id === currentDevice.id ? { ...device, ...updatedDevice } : device)
          );
          alert('Device updated successfully!');
          setOpenDialog(false);
          loadData();
          refreshData();
          return;
        }

        const prevMult = currentDevice?.multiplier !== undefined && currentDevice?.multiplier !== null ? Number(currentDevice.multiplier) : 1;
        const nextMult = apiData.multiplier !== undefined && apiData.multiplier !== null ? Number(apiData.multiplier) : 1;

        if (Math.abs(prevMult - nextMult) > 1e-12) {
          setPendingUpdatePayload(apiData);
          setMultiplierApplyMode('');
          setMultiplierEarliestDate('');
          setMultiplierPromptOpen(true);
          loadMultiplierPreview(currentDevice.id, prevMult, nextMult);

          // Pre-fetch earliest reading date for clearer UX
          (async () => {
            try {
              const earliest = await deviceService.fetchDeviceReadings(currentDevice.id, { limit: 1, order: 'asc' });
              const rt = Array.isArray(earliest) && earliest[0]?.readingTime ? String(earliest[0].readingTime) : '';
              const d = rt ? rt.split('T')[0] : '';
              setMultiplierEarliestDate(d);
            } catch (e) {
              setMultiplierEarliestDate('');
            }
          })();
          return;
        }

        // Call API to update the device
        const updatedDevice = await deviceService.updateDevice(currentDevice.id, apiData);
        
        // Update the devices list with the updated device
        setDevices(prevDevices => 
          prevDevices.map(device => device.id === currentDevice.id ? {...device, ...updatedDevice} : device)
        );
        setFilteredDevices(prevFiltered => 
          prevFiltered.map(device => device.id === currentDevice.id ? {...device, ...updatedDevice} : device)
        );
        
        // Show success message
        alert('Device updated successfully!');
      } else if (dialogType === 'delete') {
        if (!isAdmin) return;
        // Call API to delete the device
        await deviceService.deleteDevice(currentDevice.id);
        
        // Remove the device from the devices list
        setDevices(prevDevices => prevDevices.filter(device => device.id !== currentDevice.id));
        setFilteredDevices(prevFiltered => prevFiltered.filter(device => device.id !== currentDevice.id));
        
        // Show success message
        alert('Device deleted successfully!');
      }
      
      // Close the dialog
      setOpenDialog(false);
      
      // Reload data to ensure everything is up to date
      loadData();
      
      // Trigger the global refresh to update all app data
      refreshData();
    } catch (error) {
      console.error('Error processing device operation:', error);
      alert(`Error: ${error.message || 'An unknown error occurred'}`);
    }
  };

  const handleRecalculateUsage = async () => {
    try {
      if (!currentDevice?.id) return;
      setRecalculateUsageSaving(true);
      const result = await deviceService.recalculateDeviceUsage(currentDevice.id);
      const msg = `Usage recalculated. Updated readings: ${Number(result?.repairedReadings || 0)}, daily usage days: ${Number(result?.dailyUsageDays || 0)}`;
      alert(msg);
      loadData();
      refreshData();
    } catch (error) {
      console.error('Error recalculating device usage:', error);
      alert(`Error recalculating usage: ${error.message || 'Unknown error'}`);
    } finally {
      setRecalculateUsageSaving(false);
    }
  };

  const handleConfirmMultiplierPrompt = async () => {
    try {
      if (!multiplierApplyMode) return;
      if (!pendingUpdatePayload || !currentDevice?.id) return;
      setMultiplierPromptSaving(true);

      const todayStr = new Date().toISOString().split('T')[0];
      let effectiveFrom = todayStr;

      if (multiplierApplyMode === 'retroactive') {
        if (multiplierEarliestDate) {
          effectiveFrom = multiplierEarliestDate;
        } else {
          try {
            const earliest = await deviceService.fetchDeviceReadings(currentDevice.id, { limit: 1, order: 'asc' });
            const rt = Array.isArray(earliest) && earliest[0]?.readingTime ? String(earliest[0].readingTime) : '';
            effectiveFrom = rt ? rt.split('T')[0] : todayStr;
          } catch (e) {
            effectiveFrom = todayStr;
          }
        }
      } else if (multiplierApplyMode === 'custom') {
        effectiveFrom = multiplierCustomDate || todayStr;
      }

      const payload = {
        ...pendingUpdatePayload,
        multiplierEffectiveFrom: effectiveFrom,
        multiplierEffectiveTo: multiplierCustomEndDate || undefined
      };

      const updatedDevice = await deviceService.updateDevice(currentDevice.id, payload);

      setDevices(prevDevices =>
        prevDevices.map(device => device.id === currentDevice.id ? { ...device, ...updatedDevice } : device)
      );
      setFilteredDevices(prevFiltered =>
        prevFiltered.map(device => device.id === currentDevice.id ? { ...device, ...updatedDevice } : device)
      );

      alert('Device updated successfully!');
      setMultiplierPromptOpen(false);
      setPendingUpdatePayload(null);
      setOpenDialog(false);
      loadData();
      refreshData();
    } catch (error) {
      console.error('Error updating device multiplier:', error);
      alert(`Error: ${error.message || 'An unknown error occurred'}`);
    } finally {
      setMultiplierPromptSaving(false);
    }
  };

  // Format numbers with commas and fixed decimal places
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    
    if (typeof num === 'string') {
      num = parseFloat(num);
    }
    
    if (isNaN(num)) return '0';
    
    // Format with commas and 2 decimal places
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Get site name by ID
  const getSiteName = (siteId) => {
    const site = sites.find(site => site.id === siteId);
    return site ? site.description : 'Unknown Site';
  };

  const csvEscape = (value) => {
    if (value === null || value === undefined) return '';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const downloadCsv = (filename, headers, rows) => {
    const csvLines = [];
    csvLines.push(headers.map(csvEscape).join(','));
    rows.forEach((row) => {
      csvLines.push(headers.map((header) => csvEscape(row[header])).join(','));
    });
    const csv = `\ufeff${csvLines.join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const buildDeviceExportRows = (devicesToExport) => {
    return (Array.isArray(devicesToExport) ? devicesToExport : []).map((device) => {
      const siteNames = Array.isArray(device?.sites)
        ? device.sites.map((s) => s?.description).filter(Boolean).join(' | ')
        : '';
      return {
        serialNumber: device?.serialNumber || '',
        description: device?.description || '',
        location: device?.location || '',
        sites: siteNames,
        deviceType: device?.deviceType || '',
        isActive: device?.isActive === false ? 'false' : 'true',
        multiplier: device?.multiplier ?? '',
        dailyThresholdLiters: device?.dailyThresholdLiters ?? '',
        highUsageThresholdLiters: device?.highUsageThresholdLiters ?? '',
        accountNumber: device?.accountNumber ?? device?.metadata?.accountNumber ?? '',
        latitude: device?.latitude ?? '',
        longitude: device?.longitude ?? '',
        parentId: device?.parentId ?? '',
        firstReportedAt: device?.firstReportedAt || '',
        lastReading: device?.lastReading || ''
      };
    });
  };

  const handleExportDevicesCSV = async () => {
    try {
      setDevicesCsvExporting(true);
      const hasDropdownFilters =
        (Array.isArray(filterSiteIds) && filterSiteIds.length > 0)
        || (Array.isArray(filterDeviceTypes) && filterDeviceTypes.length > 0)
        || (String(filterIsActive || '').trim() !== '');
      const hasSearch = String(searchTerm || '').trim() !== '';
      const shouldClientFilterAll = hasSearch || hasDropdownFilters;

      let devicesToExport = Array.isArray(devices) ? devices : [];
      if (!shouldClientFilterAll) {
        const serverSortable = new Set([
          'serialNumber',
          'description',
          'location',
          'deviceType',
          'isActive',
          'lastReading',
          'firstReportedAt',
          'multiplier',
          'dailyThresholdLiters',
          'site'
        ]);
        const resp = await deviceService.fetchDevices({
          limit: 10000,
          offset: 0,
          lean: true,
          siteIds: (Array.isArray(filterSiteIds) && filterSiteIds.length > 0) ? filterSiteIds.join(',') : undefined,
          deviceTypes: (Array.isArray(filterDeviceTypes) && filterDeviceTypes.length > 0) ? filterDeviceTypes.join(',') : undefined,
          isActive: String(filterIsActive || '').trim() !== '' ? filterIsActive : undefined,
          sortBy: (sortBy && serverSortable.has(sortBy)) ? sortBy : undefined,
          sortDir: (sortBy && serverSortable.has(sortBy)) ? sortDir : undefined
        });
        devicesToExport = resp?.devices || [];
      }

      const rows = buildDeviceExportRows(devicesToExport);
      if (rows.length === 0) {
        showSnackbar('No devices match the current filters', 'warning');
        return;
      }

      const headers = [
        'serialNumber',
        'description',
        'location',
        'sites',
        'deviceType',
        'isActive',
        'multiplier',
        'dailyThresholdLiters',
        'highUsageThresholdLiters',
        'accountNumber',
        'latitude',
        'longitude',
        'parentId',
        'firstReportedAt',
        'lastReading'
      ];
      const stamp = new Date().toISOString().split('T')[0];
      downloadCsv(`devices_${stamp}.csv`, headers, rows);
    } catch (error) {
      console.error('Error exporting devices CSV:', error);
      showSnackbar('Failed to export devices CSV', 'error');
    } finally {
      setDevicesCsvExporting(false);
    }
  };

  // Removed unused device helper functions

  

  // Pagination controls
  const totalPages = Math.ceil(totalDevices / pageSize);


  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 },
      maxWidth: '100%',
      mx: 'auto',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          mb: 2
        }}
      >
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          component="h1"
          gutterBottom
          sx={{ mr: 1 }}
        >
          Devices
        </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            flexBasis: { xs: '100%', sm: 'auto' },
            mt: { xs: 0.5, sm: 0 },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' }
            }}
          >
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportDevicesCSV}
              size={isMobile ? 'small' : 'medium'}
              disabled={devicesCsvExporting || loading}
            >
              {devicesCsvExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            {isAdminOrOperator && (
              <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleAddDevice}
                size={isMobile ? 'small' : 'medium'}
              >
                Add Device
              </Button>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={openBulkDialog}
                size={isMobile ? 'small' : 'medium'}
              >
                Bulk Edit
              </Button>
            </>
          )}
        </Box>
      </Box>
      
      {/* Search bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              placeholder="Search Devices..."
              value={searchTerm}
              onChange={handleSearchChange}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} edge="end" size={isMobile ? 'small' : 'medium'}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="filter-site-ids-label">Sites</InputLabel>
              <Select
                labelId="filter-site-ids-label"
                multiple
                value={filterSiteIds}
                onChange={handleFilterSiteIdsChange}
                input={<OutlinedInput label="Sites" />}
                renderValue={(selected) => {
                  const ids = Array.isArray(selected) ? selected.map((id) => String(id)) : [];
                  const byId = new Map((sites || []).map((s) => [String(s?.id), s]));
                  return ids
                    .map((id) => id === '__unassigned__' ? 'Not assigned' : (byId.get(id)?.description || id))
                    .join(', ');
                }}
              >
                <MenuItem key="__unassigned__" value="__unassigned__">
                  <Checkbox checked={filterSiteIds.indexOf('__unassigned__') > -1} />
                  <Typography variant="body2" noWrap>Not assigned</Typography>
                </MenuItem>
                {(sites || []).map((s) => (
                  <MenuItem key={String(s.id)} value={String(s.id)}>
                    <Checkbox checked={filterSiteIds.indexOf(String(s.id)) > -1} />
                    <Typography variant="body2" noWrap>{s.description}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="filter-device-types-label">Device Types</InputLabel>
              <Select
                labelId="filter-device-types-label"
                multiple
                value={filterDeviceTypes}
                onChange={handleFilterDeviceTypesChange}
                input={<OutlinedInput label="Device Types" />}
                renderValue={(selected) => (Array.isArray(selected) ? selected.join(', ') : '')}
              >
                {(() => {
                  const types = new Set(['water_meter', 'electrical_meter', 'kamstrup_adv']);
                  (devices || []).forEach((d) => {
                    if (!d?.deviceType) return;
                    types.add(String(d.deviceType));
                  });
                  (filterDeviceTypes || []).forEach((t) => {
                    if (!t) return;
                    types.add(String(t));
                  });
                  return Array.from(types).sort();
                })().map((t) => (
                  <MenuItem key={String(t)} value={String(t)}>
                    <Checkbox checked={filterDeviceTypes.indexOf(String(t)) > -1} />
                    <Typography variant="body2">
                      {t === 'water_meter' ? 'Water Meter' : t === 'electrical_meter' ? 'Electrical Meter' : t === 'kamstrup_adv' ? 'Kamstrup ADV' : String(t)}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="filter-is-active-label">Active</InputLabel>
              <Select
                labelId="filter-is-active-label"
                value={filterIsActive}
                label="Active"
                onChange={handleFilterIsActiveChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      
      {/* Devices list - Table for desktop, Cards for mobile */}
      {isMobile ? (
        // Mobile Card View
        <Box sx={{ mb: 2 }}>
          {filteredDevices.length > 0 ? (
            filteredDevices.map((device) => (
              <DeviceCard
                key={device.id}
                device={device}
                usageData={deviceUsageData[device.id] || {}}
                onEdit={handleEditDevice}
                onDelete={handleDeleteDevice}
                onInvite={isAdminOrSuper ? handleCreateInviteLink : undefined}
                deleteDisabled={!isAdmin}
                onSiteClick={handleSiteClick}
                onClick={() => handleDeviceClick(device.id)}
              />
            ))
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No devices found
              </Typography>
              {isAdminOrOperator && (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddDevice}
                  >
                    Add Device
                  </Button>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} sx={{ overflowX: 'hidden' }}>
          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortBy === 'serialNumber' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'serialNumber'}
                    direction={sortBy === 'serialNumber' ? sortDir : 'asc'}
                    onClick={() => requestSort('serialNumber')}
                  >
                    Serial Number
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'description' ? sortDir : false} sx={{ width: 320 }}>
                  <TableSortLabel
                    active={sortBy === 'description'}
                    direction={sortBy === 'description' ? sortDir : 'asc'}
                    onClick={() => requestSort('description')}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'location' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'location'}
                    direction={sortBy === 'location' ? sortDir : 'asc'}
                    onClick={() => requestSort('location')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'site' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'site'}
                    direction={sortBy === 'site' ? sortDir : 'asc'}
                    onClick={() => requestSort('site')}
                  >
                    Site
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'firstReportedAt' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'firstReportedAt'}
                    direction={sortBy === 'firstReportedAt' ? sortDir : 'asc'}
                    onClick={() => requestSort('firstReportedAt')}
                  >
                    First Reported
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'todayReadingCount' ? sortDir : false} sx={{ width: 110 }}>
                  <TableSortLabel
                    active={sortBy === 'todayReadingCount'}
                    direction={sortBy === 'todayReadingCount' ? sortDir : 'asc'}
                    onClick={() => requestSort('todayReadingCount')}
                  >
                    Today Readings
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'todayUsage' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'todayUsage'}
                    direction={sortBy === 'todayUsage' ? sortDir : 'asc'}
                    onClick={() => requestSort('todayUsage')}
                  >
                    Today Usage
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'yesterdayUsage' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'yesterdayUsage'}
                    direction={sortBy === 'yesterdayUsage' ? sortDir : 'asc'}
                    onClick={() => requestSort('yesterdayUsage')}
                  >
                    Yesterday
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'last30DaysUsage' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'last30DaysUsage'}
                    direction={sortBy === 'last30DaysUsage' ? sortDir : 'asc'}
                    onClick={() => requestSort('last30DaysUsage')}
                  >
                    30 Days
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'multiplier' ? sortDir : false} sx={{ width: 90 }}>
                  <TableSortLabel
                    active={sortBy === 'multiplier'}
                    direction={sortBy === 'multiplier' ? sortDir : 'asc'}
                    onClick={() => requestSort('multiplier')}
                  >
                    Multiplier
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDevices.length > 0 ? (
                filteredDevices.map((device) => (
                  <TableRow 
                    key={device.id}
                    hover
                    onClick={() => handleDeviceClick(device.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{device.serialNumber}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {device?.deviceType === 'electrical_meter' ? (
                          <Bolt sx={{ color: theme.palette.warning.main, fontSize: '1rem' }} />
                        ) : (
                          <WaterDrop sx={{ color: theme.palette.primary.main, fontSize: '1rem' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        >
                          {device.description}
                        </Typography>
                        {device?.deviceType === 'kamstrup_adv' && (!device?.metadata?.decryptKey || device?.metadata?.needsDecryptKey) && (
                          <Tooltip title="Decryption key required - no readings will be stored until configured">
                            <VpnKey 
                              sx={{ 
                                fontSize: '1rem',
                                color: 'error.main',
                                ml: 0.5
                              }} 
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                        {device.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {Array.isArray(device.sites) && device.sites.length > 0 ? (
                        <Tooltip title={device.sites.map((s) => s.description).filter(Boolean).join(', ')}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
                            {device.sites.map((s, idx) => (
                              <React.Fragment key={s.id}>
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  noWrap
                                  onClick={(e) => handleSiteClick(s.id, e)}
                                  sx={{ cursor: 'pointer', minWidth: 0 }}
                                >
                                  {s.description}
                                </Typography>
                                {idx < device.sites.length - 1 && (
                                  <Typography variant="body2" color="text.secondary">,</Typography>
                                )}
                              </React.Fragment>
                            ))}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={device.firstReportedAt ? 'text.primary' : 'text.secondary'} noWrap>
                        {device.firstReportedAt ? new Date(device.firstReportedAt).toISOString().slice(0, 10) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {deviceUsageData[device.id] ? (
                        <Typography variant="body2">
                          {deviceUsageData[device.id].todayReadingCount || 0}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No data
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {deviceUsageData[device.id] ? (
                        <Typography variant="body2">
                          {formatNumber(deviceUsageData[device.id].todayUsage)} {getDeviceUnit(device)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No data
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {deviceUsageData[device.id] ? (
                        <Typography variant="body2">
                          {formatNumber(deviceUsageData[device.id].yesterdayUsage)} {getDeviceUnit(device)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No data
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {deviceUsageData[device.id] ? (
                        <Typography variant="body2">
                          {formatNumber(deviceUsageData[device.id].last30DaysUsage)} {getDeviceUnit(device)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No data
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const n = device.multiplier !== undefined && device.multiplier !== null ? Number(device.multiplier) : 1;
                        if (!Number.isFinite(n)) return '1.00';
                        return n.toFixed(2);
                      })()}
                    </TableCell>
                    <TableCell>
                      {isAdminOrSuper && (
                        <Tooltip title="Invite link">
                          <IconButton
                            color="primary"
                            onClick={(e) => handleCreateInviteLink(device, e)}
                            size="small"
                          >
                            <QrCode2 />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton 
                        color="primary" 
                        onClick={(e) => handleEditDevice(device, e)}
                        size="small"
                      >
                        <Edit />
                      </IconButton>
                      <Tooltip title="Delete Device">
                        <span>
                          <IconButton 
                            color="error" 
                            disabled={!isAdmin}
                            onClick={(e) => {
                              if (!isAdmin) return;
                              handleDeleteDevice(device, e);
                            }}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No devices found
                    </Typography>
                    {isAdminOrOperator && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleAddDevice}
                        startIcon={<Add />}
                        sx={{ mt: 1 }}
                      >
                        Add Your First Device
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3 }}>
          <Button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            sx={{ mr: 2 }}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ mx: 2 }}>
            Page {page} of {totalPages}
          </Typography>
          <Button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            sx={{ ml: 2 }}
          >
            Next
          </Button>
        </Box>
      )}
      
      {/* Add/Edit Device Dialog */}
      <Dialog 
        open={openDialog && (dialogType === 'add' || dialogType === 'edit')} 
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        scroll="paper"
        maxWidth={isMobile ? "xs" : "sm"}
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 600 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>
          {dialogType === 'add' ? 'Add New Device' : (isEndUser ? 'Edit Threshold' : 'Edit Device')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {isEndUser && dialogType === 'edit' ? (
              <Grid item xs={12}>
                <TextField
                  name="dailyThresholdLiters"
                  label={`Daily Threshold (${deviceFormData.deviceType === 'electrical_meter' ? 'kWh' : 'L'})`}
                  type="number"
                  inputProps={{ step: 0.1, min: 0 }}
                  fullWidth
                  value={deviceFormData.dailyThresholdLiters}
                  onChange={handleDeviceFormChange}
                  helperText="If set, exceeding this daily usage will create a notification"
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    name="serialNumber"
                    label="Serial Number"
                    fullWidth
                    required
                    disabled={!isAdminOrOperator}
                    value={deviceFormData.serialNumber}
                    onChange={handleDeviceFormChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="accountNumber"
                    label="Account Number"
                    fullWidth
                    value={deviceFormData.accountNumber}
                    onChange={handleDeviceFormChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label="Description"
                    fullWidth
                    value={deviceFormData.description}
                    onChange={handleDeviceFormChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StructuredLocationFields
                    value={deviceFormData.location}
                    onChange={(nextLocation) => setDeviceFormData((p) => ({ ...p, location: nextLocation }))}
                    title="Location"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="latitude"
                        label="Latitude (Optional)"
                        type="number"
                        inputProps={{ step: 0.0000001, min: -90, max: 90 }}
                        fullWidth
                        value={deviceFormData.latitude}
                        onChange={handleDeviceFormChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="longitude"
                        label="Longitude (Optional)"
                        type="number"
                        inputProps={{ step: 0.0000001, min: -180, max: 180 }}
                        fullWidth
                        value={deviceFormData.longitude}
                        onChange={handleDeviceFormChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Use current GPS">
                          <span>
                            <IconButton onClick={handleUseCurrentGps} disabled={gpsLocating}>
                              {gpsLocating ? <CircularProgress size={20} /> : <MyLocation />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="multiplier"
                    label="Multiplier"
                    type="number"
                    inputProps={{ step: 0.0001, min: 0 }}
                    fullWidth
                    disabled={!isAdminOrOperator}
                    value={deviceFormData.multiplier}
                    onChange={handleDeviceFormChange}
                    helperText="Multiply raw readings by this value (e.g., 1000 for x1000)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="highUsageThresholdLiters"
                    label={`High Usage Threshold (${deviceFormData.deviceType === 'electrical_meter' ? 'kWh' : 'L'})`}
                    type="number"
                    inputProps={{ step: 0.1, min: 0 }}
                    fullWidth
                    value={deviceFormData.highUsageThresholdLiters}
                    onChange={handleDeviceFormChange}
                    helperText="If a single reading exceeds this usage, a high usage notification may be created"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="dailyThresholdLiters"
                    label={`Daily Threshold (${deviceFormData.deviceType === 'electrical_meter' ? 'kWh' : 'L'})`}
                    type="number"
                    inputProps={{ step: 0.1, min: 0 }}
                    fullWidth
                    value={deviceFormData.dailyThresholdLiters}
                    onChange={handleDeviceFormChange}
                    helperText="If set, exceeding this daily usage will create a notification"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="decryptKey"
                    label="Decryption Key (Kamstrup Devices)"
                    fullWidth
                    disabled={!isAdminOrOperator}
                    value={deviceFormData.decryptKey}
                    onChange={handleDeviceFormChange}
                    helperText="32-character hex key for Kamstrup Advanced meters (leave empty if not applicable)"
                    placeholder="e.g., 0123456789ABCDEF0123456789ABCDEF"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={sites}
                    disabled={!isAdminOrOperator}
                    getOptionLabel={(option) => option.description || ''}
                    value={sites.filter(site => (deviceFormData.siteIds || []).includes(site.id))}
                    onChange={handleSiteSelection}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sites"
                        placeholder="Select one or more sites"
                        fullWidth
                        helperText="A meter can be assigned to more than one site (e.g. bulk on one, sub-meter on another)"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    disabled={!isAdminOrOperator}
                    options={parentDeviceOptions.filter(d => String(d.id) !== String(currentDevice?.id || 'new'))}
                    getOptionLabel={(option) => {
                      const serial = option?.serialNumber ? String(option.serialNumber) : '';
                      const desc = option?.description ? String(option.description) : '';
                      const siteDesc = option?.sites?.[0]?.description ? String(option.sites[0].description) : '';

                      const main = serial && desc ? `${serial} - ${desc}` : (desc || serial);
                      return siteDesc ? `${main} (${siteDesc})` : main;
                    }}
                    value={parentDeviceOptions.find(device => String(device.id) === String(deviceFormData.parentId)) || null}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    onChange={handleParentDeviceSelection}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Parent Device (Optional)"
                        fullWidth
                        helperText="Select a parent device to create a hierarchy"
                        disabled={parentDeviceOptionsLoading}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!isAdminOrOperator}>
                    <InputLabel id="device-type-label">Device Type</InputLabel>
                    <Select
                      labelId="device-type-label"
                      name="deviceType"
                      value={deviceFormData.deviceType}
                      label="Device Type"
                      onChange={handleDeviceFormChange}
                    >
                      <MenuItem value="water_meter">Water Meter</MenuItem>
                      <MenuItem value="electrical_meter">Electrical Meter</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={deviceFormData.isActive}
                        disabled={!isAdminOrOperator}
                        onChange={(e) => setDeviceFormData({
                          ...deviceFormData,
                          isActive: e.target.checked
                        })}
                        name="isActive"
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogType === 'edit' && isAdminOrOperator && (
            <Button
              onClick={handleRecalculateUsage}
              variant="outlined"
              disabled={recalculateUsageSaving || !currentDevice?.id}
            >
              {recalculateUsageSaving ? 'Recalculating...' : 'Recalculate Usage'}
            </Button>
          )}
          <Button onClick={handleSaveForm} variant="contained" color="primary">
            {dialogType === 'add' ? 'Add Device' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={multiplierPromptOpen}
        onClose={handleCloseMultiplierPrompt}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 450 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>Recalculate Previous Usage?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You changed the multiplier. This affects how usage is calculated from raw readings.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2">Preview (latest 2 readings)</Typography>
            {multiplierPreview.loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading preview…</Typography>
              </Box>
            ) : multiplierPreview.error ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {multiplierPreview.error}
              </Typography>
            ) : multiplierPreview.rows.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                {multiplierPreview.rows.map((row, index) => (
                  <Box key={`${row.toReadingTime}-${index}`} sx={{ display: 'grid', gap: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">
                      From {new Date(row.fromReadingTime).toLocaleString()} ({row.fromReadingValue.toFixed(3)})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      To {new Date(row.toReadingTime).toLocaleString()} ({row.toReadingValue.toFixed(3)})
                    </Typography>
                    <Typography variant="body2">
                      Raw usage: {row.rawUsage.toFixed(3)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Old result ({multiplierPreview.previousMultiplier}x): {row.previousUsage.toFixed(3)}
                    </Typography>
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                      New result ({multiplierPreview.nextMultiplier}x): {row.nextUsage.toFixed(3)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No preview available.
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="multiplier-apply-mode-label" shrink>Apply change</InputLabel>
              <Select
                labelId="multiplier-apply-mode-label"
                value={multiplierApplyMode}
                label="Apply change"
                onChange={(e) => setMultiplierApplyMode(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return 'Select an option…';
                  if (selected === 'forward') return 'No — only apply from today onward';
                  if (selected === 'retroactive') return 'Yes — recalculate from earliest reading';
                  if (selected === 'custom') return 'Select a specific date';
                  return String(selected);
                }}
              >
                <MenuItem value="" disabled>Select an option…</MenuItem>
                <MenuItem value="forward">No — only apply from today onward</MenuItem>
                <MenuItem value="retroactive">Yes — recalculate from earliest reading</MenuItem>
                <MenuItem value="custom">Select a specific date</MenuItem>
              </Select>
            </FormControl>
            {multiplierApplyMode === 'custom' && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Effective From Date"
                  value={multiplierCustomDate}
                  onChange={(e) => setMultiplierCustomDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
            {!!multiplierApplyMode && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Effective To Date (optional)"
                  value={multiplierCustomEndDate}
                  onChange={(e) => setMultiplierCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                From today: {new Date().toISOString().split('T')[0]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Earliest reading: {multiplierEarliestDate || 'Unknown'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMultiplierPrompt} disabled={multiplierPromptSaving}>Cancel</Button>
          <Button onClick={handleConfirmMultiplierPrompt} variant="contained" disabled={multiplierPromptSaving || !multiplierApplyMode}>Apply</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      {!isEndUser && (
        <Dialog 
          open={bulkDialogOpen} 
          onClose={() => setBulkDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: isMobile ? 0 : 2,
              m: 0,
              width: '100%',
              maxHeight: isMobile ? '100dvh' : 'calc(100vh - 64px)',
              height: isMobile ? '100%' : 'auto',
            },
            '& .MuiDialogContent-root': {
              p: isMobile ? 2 : 3,
            },
            '& .MuiDialogActions-root': {
              p: isMobile ? 2 : 3,
              pt: 1,
            },
          }}
        >
        <DialogTitle>Bulk Edit Devices</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Search across all devices and select which devices to update. Serial Number cannot be changed.
          </DialogContentText>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              sx={{ minWidth: 260, flex: 1 }}
              options={sites}
              getOptionLabel={(option) => option?.description || ''}
              value={bulkSite}
              onChange={async (e, newValue) => {
                const nextSite = newValue || null;
                setBulkSite(nextSite);
                if (!nextSite?.id) return;
                // Site-driven bulk edits are common; auto-select all devices at the chosen site.
                const siteDevices = await loadBulkSiteDevices(nextSite.id);
                setBulkSelectedDevices((prev) => mergeDevicesById(prev, siteDevices));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Site"
                  placeholder="Select a site to auto-add all its devices"
                />
              )}
            />
            <Button
              variant="outlined"
              disabled={!bulkSite || bulkLoading}
              onClick={async () => {
                const siteDevices = await loadBulkSiteDevices(bulkSite?.id);
                setBulkSelectedDevices((prev) => mergeDevicesById(prev, siteDevices));
              }}
            >
              Add Site Devices
            </Button>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Search all devices"
              placeholder="Search by description, serial, location, or site"
              value={bulkSearchTerm}
              onChange={(e) => setBulkSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runBulkSearch(); }}
            />
            <Button variant="contained" onClick={runBulkSearch} disabled={bulkLoading}>
              {bulkLoading ? 'Searching...' : 'Search'}
            </Button>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2">Search matches: {bulkSearchResults.length}</Typography>
            <Typography variant="body2">Selected: {bulkSelectedDevices.length}</Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={bulkSelectedDevices.length === 0 || bulkLoading}
              onClick={() => setBulkSelectedDevices([])}
            >
              Clear Selection
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Search results (tick to add/remove):
            </Typography>
            <Box
              sx={{
                maxHeight: 220,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.5
              }}
            >
              {bulkSearchResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  No devices found.
                </Typography>
              ) : (
                bulkSearchResults.slice(0, 200).map((device) => {
                  const serial = device?.serialNumber ? String(device.serialNumber) : '';
                  const desc = device?.description ? String(device.description) : '';
                  const location = device?.location ? String(device.location) : '';
                  const siteName = device?.sites?.[0]?.description ? String(device.sites[0].description) : 'No site';
                  const label = serial && desc ? `${serial} - ${desc}` : (desc || serial || String(device?.id || ''));

                  return (
                    <FormControlLabel
                      key={String(device?.id)}
                      control={(
                        <Checkbox
                          checked={isBulkDeviceSelected(device?.id)}
                          onChange={(e) => toggleBulkDeviceSelection(device, e.target.checked)}
                        />
                      )}
                      label={(
                        <Box>
                          <Typography variant="body2">{label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {siteName}{location ? ` • ${location}` : ''}
                          </Typography>
                        </Box>
                      )}
                    />
                  );
                })
              )}
            </Box>
            {bulkSearchResults.length > 200 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Showing first 200 matches. Refine search to narrow results.
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Devices to update:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 160, overflowY: 'auto' }}>
              {bulkSelectedDevices.map((d) => {
                const serial = d?.serialNumber ? String(d.serialNumber) : '';
                const desc = d?.description ? String(d.description) : '';
                const label = serial && desc ? `${serial} - ${desc}` : (desc || serial || String(d?.id || ''));
                return (
                  <Chip
                    key={String(d?.id)}
                    label={label}
                    onDelete={() => setBulkSelectedDevices((prev) => prev.filter((x) => String(x?.id) !== String(d?.id)))}
                    deleteIcon={<Clear />}
                    size="small"
                  />
                );
              })}
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={bulkFields.description} onChange={(e) => setBulkFields({ ...bulkFields, description: e.target.checked })} />}
                  label="Update Description"
                />
                <TextField fullWidth disabled={!bulkFields.description} label="Description" value={bulkValues.description} onChange={(e) => setBulkValues({ ...bulkValues, description: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={bulkFields.location} onChange={(e) => setBulkFields({ ...bulkFields, location: e.target.checked })} />}
                  label="Update Location"
                />
                <Box sx={{ opacity: bulkFields.location ? 1 : 0.6, pointerEvents: bulkFields.location ? 'auto' : 'none' }}>
                  <StructuredLocationFields
                    value={bulkValues.location}
                    onChange={(nextLocation) => setBulkValues({ ...bulkValues, location: nextLocation })}
                    title="Location"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={bulkFields.deviceType} onChange={(e) => setBulkFields({ ...bulkFields, deviceType: e.target.checked })} />}
                  label="Update Device Type"
                />
                <FormControl fullWidth disabled={!bulkFields.deviceType}>
                  <InputLabel>Device Type</InputLabel>
                  <Select
                    label="Device Type"
                    value={bulkValues.deviceType}
                    onChange={(e) => setBulkValues({ ...bulkValues, deviceType: e.target.value })}
                  >
                    <MenuItem value="water_meter">Water meter</MenuItem>
                    <MenuItem value="electrical_meter">Electrical meter</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={bulkFields.isActive} onChange={(e) => setBulkFields({ ...bulkFields, isActive: e.target.checked })} />}
                  label="Update Status"
                />
                <FormControlLabel
                  control={<Switch disabled={!bulkFields.isActive} checked={bulkValues.isActive} onChange={(e) => setBulkValues({ ...bulkValues, isActive: e.target.checked })} />}
                  label={bulkValues.isActive ? 'Active' : 'Inactive'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={bulkFields.multiplier} onChange={(e) => {
                    const checked = e.target.checked;
                    setBulkFields({ ...bulkFields, multiplier: checked });
                  }} />}
                  label="Update Multiplier"
                />
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ step: 0.0001, min: 0 }}
                  disabled={!bulkFields.multiplier}
                  label="Multiplier"
                  value={bulkValues.multiplier}
                  onChange={(e) => setBulkValues({ ...bulkValues, multiplier: e.target.value })}
                />
                <FormControl fullWidth disabled={!bulkFields.multiplier} sx={{ mt: 1 }}>
                  <InputLabel>Select Devices By Date</InputLabel>
                  <Select
                    label="Select Devices By Date"
                    value={bulkValues.multiplierDeviceDateMode || 'before_or_on'}
                    onChange={(e) => setBulkValues({ ...bulkValues, multiplierDeviceDateMode: e.target.value })}
                  >
                    <MenuItem value="before_or_on">Before this date</MenuItem>
                    <MenuItem value="after_or_on">After this date</MenuItem>
                    <MenuItem value="between">Between 2 dates</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  type="date"
                  disabled={!bulkFields.multiplier}
                  label={bulkValues.multiplierDeviceDateMode === 'between' ? 'From Date' : 'Date'}
                  value={bulkValues.multiplierDeviceDateFrom || ''}
                  onChange={(e) => setBulkValues({ ...bulkValues, multiplierDeviceDateFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1 }}
                />
                {bulkValues.multiplierDeviceDateMode === 'between' && (
                  <TextField
                    fullWidth
                    type="date"
                    disabled={!bulkFields.multiplier}
                    label="To Date"
                    value={bulkValues.multiplierDeviceDateTo || ''}
                    onChange={(e) => setBulkValues({ ...bulkValues, multiplierDeviceDateTo: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mt: 1 }}
                  />
                )}
                {bulkFields.multiplier && (
                  <Typography variant="caption" color={bulkMultiplierSelection.error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 1 }}>
                    {bulkMultiplierSelection.error
                      ? bulkMultiplierSelection.error
                      : bulkMultiplierSelection.selectedCount === 0
                        ? 'No devices selected yet. Search and tick devices, or select a site to auto-add its devices.'
                        : `Date filter matches ${bulkMultiplierSelection.matchingCount} of ${bulkMultiplierSelection.selectedCount} selected devices.`}
                  </Typography>
                )}
                {bulkFields.multiplier && bulkMultiplierSelection.missingDateCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {bulkMultiplierSelection.missingDateCount} selected devices have no first reported date and will be skipped.
                  </Typography>
                )}
                {bulkFields.multiplier && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    All historical usage for each matched device will be corrected, back to its first reading.
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={bulkFields.dailyThresholdLiters} onChange={(e) => setBulkFields({ ...bulkFields, dailyThresholdLiters: e.target.checked })} />}
                  label="Update Daily Threshold"
                />
                <TextField fullWidth type="number" inputProps={{ step: 0.1, min: 0 }} disabled={!bulkFields.dailyThresholdLiters} label="Daily Threshold" value={bulkValues.dailyThresholdLiters} onChange={(e) => setBulkValues({ ...bulkValues, dailyThresholdLiters: e.target.value })} placeholder="Leave blank to clear" />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={bulkFields.parentId} onChange={(e) => setBulkFields({ ...bulkFields, parentId: e.target.checked })} />}
                  label="Update Parent Device"
                />
                <Autocomplete
                  options={parentDeviceOptions}
                  getOptionLabel={(option) => {
                    const serial = option?.serialNumber ? String(option.serialNumber) : '';
                    const desc = option?.description ? String(option.description) : '';
                    const siteDesc = option?.sites?.[0]?.description ? String(option.sites[0].description) : '';

                    const main = serial && desc ? `${serial} - ${desc}` : (desc || serial);
                    return siteDesc ? `${main} (${siteDesc})` : main;
                  }}
                  value={parentDeviceOptions.find(device => String(device.id) === String(bulkValues.parentId)) || null}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  onChange={(e, newValue) => setBulkValues({ ...bulkValues, parentId: newValue ? newValue.id : '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Parent Device"
                      fullWidth
                      placeholder="Select parent device (or clear)"
                      disabled={parentDeviceOptionsLoading || !bulkFields.parentId}
                    />
                  )}
                  disabled={!bulkFields.parentId}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={bulkFields.siteIds} onChange={(e) => setBulkFields({ ...bulkFields, siteIds: e.target.checked })} />}
                  label="Update Site Associations"
                />
                <Autocomplete
                  multiple
                  options={sites}
                  getOptionLabel={(option) => option.description || ''}
                  value={sites.filter(s => bulkValues.siteIds.includes(s.id))}
                  onChange={(e, newValue) => setBulkValues({ ...bulkValues, siteIds: newValue.map(v => v.id) })}
                  renderInput={(params) => <TextField {...params} label="Sites" placeholder="Select sites" />}
                  disabled={!bulkFields.siteIds}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              bulkApplying
              || bulkSelectedDevices.length === 0
              || (!Object.values(bulkFields).some(Boolean))
              || (bulkFields.multiplier && (!!bulkMultiplierSelection.error || bulkApplyTargetCount === 0))
            }
            onClick={async () => {
              try {
                if (bulkFields.multiplier && bulkMultiplierSelection.error) {
                  setSnackbar({ open: true, message: bulkMultiplierSelection.error, severity: 'error' });
                  return;
                }

                const devicesToUpdate = bulkFields.multiplier
                  ? bulkMultiplierSelection.matchingDevices
                  : bulkSelectedDevices;
                if (!Array.isArray(devicesToUpdate) || devicesToUpdate.length === 0) {
                  setSnackbar({ open: true, message: 'No selected devices match the current bulk filters', severity: 'warning' });
                  return;
                }

                const deviceIds = devicesToUpdate.map(d => d.id);
                const payload = { deviceIds };
                if (bulkFields.description) payload.description = bulkValues.description;
                if (bulkFields.location) payload.location = bulkValues.location;
                if (bulkFields.deviceType) payload.deviceType = bulkValues.deviceType;
                if (bulkFields.isActive) payload.isActive = !!bulkValues.isActive;
                if (bulkFields.multiplier) {
                  const parsedMultiplier = Number(bulkValues.multiplier);
                  if (!Number.isFinite(parsedMultiplier) || parsedMultiplier <= 0) {
                    setSnackbar({ open: true, message: 'Please enter a valid multiplier greater than 0', severity: 'error' });
                    return;
                  }
                  payload.multiplier = parsedMultiplier;
                  // Always correct the full history for matched devices, back to each device's first reading.
                  payload.multiplierApplyMode = 'retroactive';
                }
                if (bulkFields.dailyThresholdLiters) payload.dailyThresholdLiters = bulkValues.dailyThresholdLiters === '' ? null : Number(bulkValues.dailyThresholdLiters);
                if (bulkFields.parentId) payload.parentId = bulkValues.parentId === '' ? null : String(bulkValues.parentId);
                if (bulkFields.siteIds) payload.siteIds = bulkValues.siteIds;

                setBulkApplying(true);
                const resp = await deviceService.bulkUpdateDevices(payload);
                const filteredSuffix = bulkFields.multiplier && deviceIds.length !== bulkSelectedDevices.length
                  ? ` (filtered from ${bulkSelectedDevices.length} selected)`
                  : '';
                setSnackbar({ open: true, message: `Updated ${resp.updated || 0} devices${filteredSuffix}`, severity: 'success' });
                setBulkDialogOpen(false);
                await loadData(page, pageSize, searchTerm);
              } catch (e) {
                console.error('Bulk apply failed:', e);
                setSnackbar({ open: true, message: e.message || 'Bulk apply failed', severity: 'error' });
              } finally {
                setBulkApplying(false);
              }
            }}
          >
            Apply to {bulkApplyTargetCount} devices
          </Button>
        </DialogActions>
      </Dialog>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDialog && dialogType === 'delete'} 
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        scroll="paper"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 450 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>Delete Device</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this device? This action cannot be undone and will remove all associated readings and usage data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveForm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>Device Invite Link</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Device</Typography>
              <Typography variant="body1" fontWeight={700}>{inviteDeviceLabel}</Typography>
              {!!inviteExpiresAt && (
                <Typography variant="caption" color="text.secondary">Expires: {inviteExpiresAt}</Typography>
              )}
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Invite URL</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inviteUrl}
                  InputProps={{ readOnly: true }}
                />
                <Tooltip title="Copy">
                  <IconButton color="primary" onClick={handleCopyInviteUrl}>
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {!!inviteUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <QRCode value={inviteUrl} size={220} />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Scroll to top button */}
      <ScrollTop>
        <Fab color="primary" size="small" aria-label="scroll back to top">
          <KeyboardArrowUp />
        </Fab>
      </ScrollTop>
    </Box>
  );
};

export default Devices;
